import React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuFilledIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import MuiDrawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { CSSObject, Theme, styled } from "@mui/material/styles";
import AccountMenu from "./AccountMenu";
import CorticaLogo from "./CorticaLogo";
import MenuItems from "./MenuItems";
import { NavItem } from "./navItems";
import { useSidebar } from "./SidebarContext";

// Drawer sizing, mixins and header are ported from BloomABA's Sidebar so the
// apps match visually. The only intended difference is the Cortica logo.
export const drawerWidth = 300;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

const Sidebar = ({ navItems }: { navItems: NavItem[] }) => {
  const { isOpen: open, toggle } = useSidebar();

  return (
    <Drawer variant="permanent" open={open}>
      <DrawerHeader
        sx={{ justifyContent: open ? "space-between" : "center", p: 2 }}
      >
        {open && (
          <Box sx={{ width: 148, maxWidth: 148, height: "auto", ml: 0.5 }}>
            <CorticaLogo />
          </Box>
        )}
        <IconButton
          onClick={toggle}
          size="large"
          aria-label={open ? "Collapse navigation" : "Expand navigation"}
        >
          {open ? <ChevronLeftIcon /> : <MenuFilledIcon />}
        </IconButton>
      </DrawerHeader>
      <Divider />
      <MenuItems items={navItems} open={open} />
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <AccountMenu open={open} />
    </Drawer>
  );
};

export default Sidebar;
